import { ExternalLink, Link2, Plus } from "lucide-react";
import { useState } from "react";
import type { EntityLink } from "../types";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";

type EntityLinksPanelProps = {
  objectId: string;
  links: EntityLink[];
  error?: string;
  onLoad: (objectId: string) => Promise<void>;
  onCreate: (objectId: string, input: { url: string; label?: string }) => Promise<void>;
  onDelete: (objectId: string, linkId: string) => Promise<void>;
};

export function EntityLinksPanel({ objectId, links, error, onLoad, onCreate, onDelete }: EntityLinksPanelProps) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const deleteConfirm = useSecondClickConfirm();

  const handleCreate = async () => {
    const normalizedUrl = url.trim();
    const normalizedLabel = label.trim();

    if (!normalizedUrl) {
      return;
    }

    setIsSaving(true);

    try {
      await onCreate(objectId, {
        url: normalizedUrl,
        label: normalizedLabel || undefined
      });
      setUrl("");
      setLabel("");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-line bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold text-slate-700">Link tham khảo</p>
        <button
          className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
          type="button"
          onClick={() => void onLoad(objectId)}
        >
          <Link2 size={14} aria-hidden="true" />
          Tải lại
        </button>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px_auto]">
        <input
          className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink placeholder:text-slate-400"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://..."
          aria-label="URL cần lưu"
        />
        <input
          className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink placeholder:text-slate-400"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Tên link"
          aria-label="Tên link"
        />
        <button
          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          type="button"
          disabled={!url.trim() || isSaving}
          onClick={handleCreate}
        >
          <Plus size={14} aria-hidden="true" />
          {isSaving ? "Đang lưu" : "Lưu link"}
        </button>
      </div>

      {error ? (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {error}
        </p>
      ) : null}

      <div className="mt-3 space-y-2">
        {links.length === 0 ? (
          <p className="text-xs font-semibold text-muted">Chưa có link nào. Dán URL để lưu nguồn tham khảo cho thực thể này.</p>
        ) : (
          links.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between gap-3 rounded-md border border-line bg-white px-3 py-2 text-xs font-semibold text-slate-700"
            >
              <a
                className="min-w-0 flex-1 truncate hover:text-brand"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                title={link.url}
              >
                {link.label}
              </a>
              <a
                className="shrink-0 text-muted hover:text-brand"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Mở ${link.label}`}
              >
                <ExternalLink size={14} aria-hidden="true" />
              </a>
              <button
                className={`shrink-0 ${deleteConfirm.pendingId === link.id ? "font-bold text-red-700" : "text-red-600 hover:text-red-700"}`}
                type="button"
                onClick={() => deleteConfirm.request(link.id, () => void onDelete(objectId, link.id))}
              >
                {deleteConfirm.pendingId === link.id ? "Bấm lần nữa để xóa" : "Xóa"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
